import React, { ReactNode } from 'react';
import { styled, Typography } from '@mui/material';

export const palette = {
  ink: '#1f1723',
  muted: '#6d6470',
  border: '#eadde1',
  soft: '#fff8f5',
  primary: '#7b1235',
  primaryDark: '#4a0e1a',
  success: '#11853d',
  warning: '#b85b00',
};

export const GuidedCard = styled('section')(({ theme }) => ({
  background: '#fff',
  border: '1px solid #e5e5e8',
  borderRadius: 8,
  boxShadow: '0 2px 10px rgba(24, 24, 27, 0.04)',
  overflow: 'hidden',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    borderRadius: 0,
    borderLeft: 0,
    borderRight: 0,
  },
}));

export const CardBody = styled('div')(({ theme }) => ({
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  [theme.breakpoints.down('sm')]: {
    padding: 14,
  },
}));

export const CardHeader = styled('div')({
  padding: '16px 18px',
  borderBottom: '1px solid #e5e5e8',
  background: '#fff',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
});

export const HeaderText = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

export const TitleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

export const StepBadge = styled('span')({
  width: 30,
  height: 30,
  borderRadius: '50%',
  color: '#fff',
  background: palette.primary,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 13,
  flex: '0 0 auto',
});

export const HelperNote = styled('div')({
  border: '1px solid #f3d8b8',
  background: '#fff9ef',
  color: '#7b3d00',
  borderRadius: 8,
  padding: '10px 12px',
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
});

export const SoftPanel = styled('div')({
  border: '1px solid #e5e5e8',
  background: '#fafafa',
  borderRadius: 8,
  padding: 12,
});

export const SplitGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 14,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const ThreeGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 10,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const SelectionCard = styled('label')<{ selected?: boolean }>(props => ({
  border: `1px solid ${props.selected ? palette.primary : palette.border}`,
  background: props.selected ? '#fff7fa' : '#fff',
  borderRadius: 8,
  padding: 14,
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 12,
  alignItems: 'flex-start',
  cursor: 'pointer',
  boxShadow: props.selected ? '0 10px 28px rgba(123, 18, 53, 0.12)' : 'none',
  transition: 'border-color .2s ease, box-shadow .2s ease, background .2s ease',
  '&:hover': {
    borderColor: palette.primary,
    boxShadow: '0 10px 28px rgba(123, 18, 53, 0.08)',
  },
}));

export const SelectionContent = styled('div')({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

export const PriceRange = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 10,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const PriceBox = styled('div')({
  border: `1px solid ${palette.border}`,
  borderRadius: 8,
  padding: 10,
  background: '#fff',
});

export const SummaryGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1.4fr .8fr',
  gap: 14,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const ItemRow = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
  borderBottom: `1px solid ${palette.border}`,
  '&:last-child': {
    borderBottom: 0,
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    alignItems: 'flex-start',
  },
}));

interface GuidedHeaderProps {
  step: number;
  title: string;
  description: string;
  aside?: ReactNode;
}

export const GuidedHeader: React.FC<GuidedHeaderProps> = ({ step, title, description, aside }) => (
  <CardHeader>
    <HeaderText>
      <TitleRow>
        <StepBadge>{step}</StepBadge>
        <Typography variant="h6" color={palette.ink}>
          {title}
        </Typography>
      </TitleRow>
      <Typography variant="body2" color={palette.muted}>
        {description}
      </Typography>
    </HeaderText>
    {aside}
  </CardHeader>
);
